import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Plus, Download, ExternalLink, Copy, Archive, ArchiveRestore, Trash2, AlertTriangle, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const FILTERS = ["all", "active", "inactive", "internal", "archived", "demo"];

const formatTpl = (tpl, n) => (tpl || "").replace("{n}", String(n));

// A questionnaire is classified as Demo/Test when the title is clearly a
// demo/seed/pilot fixture, OR when its question count is small (3–6) and it
// is NOT an Official imported questionnaire. Officials short-circuit to
// false so the protection rule cannot be bypassed by a low question count.
const isDemo = (q) => {
  const en = (q.title_en || "").toLowerCase().trim();
  const es = (q.title_es || "").toLowerCase().trim();
  if (en.includes("official") || es.includes("oficial")) return false;
  if (es.startsWith("encuesta piloto -") || es.startsWith("encuesta piloto-")) return true;
  const tags = ["demo", "test", "seed", "pilot survey", "(copy)"];
  if (tags.some((t) => en.includes(t))) return true;
  if (["demo", "test", "seed", "encuesta piloto", "(copy)"].some((t) => es.includes(t))) return true;
  const qc = Array.isArray(q.questions) ? q.questions.length : (q.question_count ?? 0);
  if (qc >= 3 && qc <= 6) return true;
  return false;
};

// "Official" / "Oficial" anywhere in the EN or ES title flags an imported
// production questionnaire that must be protected from accidental deletion.
const isOfficial = (q) => {
  const en = (q.title_en || "").toLowerCase();
  const es = (q.title_es || "").toLowerCase();
  return en.includes("official") || es.includes("oficial");
};

// Internal questionnaires are only deletable if the title is explicitly
// flagged as a demo/test fixture — otherwise archive only.
const isProtectedInternal = (q) => q.is_internal && !isDemo(q);

// Returns the i18n key explaining why delete is blocked, or null when the
// questionnaire IS deletable. Centralised so the card, the modal preview and
// the editor share the exact same logic.
const deleteBlockReasonKey = (q, responseCount) => {
  if (isOfficial(q)) return "admin.officialProtected";
  if (responseCount > 0) return "admin.cantDeleteWithResponses";
  if (q.is_active) return "admin.cantDeleteActive";
  if (isProtectedInternal(q)) return "admin.cantDeleteInternal";
  return null;
};

const classificationKey = (q) => {
  if (isOfficial(q)) return "admin.classificationOfficial";
  if (isDemo(q)) return "admin.classificationDemo";
  if (q.is_internal) return "admin.classificationInternal";
  return "admin.classificationDefault";
};

const statusKey = (q) => {
  if (q.status === "archived") return "admin.statusArchived";
  if (q.is_active) return "admin.statusActive";
  return "admin.statusInactive";
};

const matchesFilter = (q, f) => {
  if (f === "all") return q.status !== "archived";
  if (f === "active") return q.is_active && q.status !== "archived";
  if (f === "inactive") return !q.is_active && q.status !== "archived";
  if (f === "internal") return q.is_internal && q.status !== "archived";
  if (f === "archived") return q.status === "archived";
  if (f === "demo") return isDemo(q) && q.status !== "archived";
  return true;
};

const AdminQuestionnaires = () => {
  const { t, lang, pickI18n, tQuestionnaireTitle } = useLang();
  const [items, setItems] = useState([]);
  const [archivedItems, setArchivedItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(null); // { kind: 'delete'|'archive'|'restore', q }
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/questionnaires"),
      api.get("/admin/questionnaires?only_archived=true"),
    ]);
    setItems(a.data || []);
    setArchivedItems(b.data || []);
  };

  useEffect(() => { reload(); }, []);

  const counts = useMemo(() => {
    const all = [...items, ...archivedItems];
    return {
      all: items.length, // hides archived
      active: items.filter((q) => q.is_active).length,
      inactive: items.filter((q) => !q.is_active).length,
      internal: items.filter((q) => q.is_internal).length,
      archived: archivedItems.length,
      demo: all.filter((q) => isDemo(q) && q.status !== "archived").length,
    };
  }, [items, archivedItems]);

  const visible = useMemo(() => {
    const list = filter === "archived" ? archivedItems : items;
    return list.filter((q) => matchesFilter(q, filter));
  }, [items, archivedItems, filter]);

  const cloneQuestionnaire = async (qid) => {
    try {
      const { data } = await api.post(`/admin/questionnaires/${qid}/clone`);
      setItems((arr) => [data, ...arr]);
      toast.success(t("phase2.questionnaireCloned"));
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  const downloadResponses = (qid) => {
    const token = localStorage.getItem("tsw_token");
    fetch(`${API_BASE}/admin/export/responses.csv?questionnaire_id=${qid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = `responses_${qid}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const askConfirm = (kind, q) => {
    if (kind === "delete" && q.is_active) {
      toast.error(t("admin.cantDeleteActive"));
      return;
    }
    setConfirm({ kind, q });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { kind, q } = confirm;
    setBusy(true);
    try {
      if (kind === "delete") {
        await api.delete(`/admin/questionnaires/${q.id}`);
        toast.success(t("admin.deleteSuccess"));
      } else if (kind === "archive") {
        await api.post(`/admin/questionnaires/${q.id}/archive`);
        toast.success(t("admin.archiveSuccess"));
      } else if (kind === "restore") {
        await api.post(`/admin/questionnaires/${q.id}/restore`);
        toast.success(t("admin.restoreSuccess"));
      }
      setConfirm(null);
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.questionnaires")}</h1>
        </div>
        <Link to="/admin/questionnaires/new" className="tsw-btn-primary" data-testid="questionnaire-new">
          <Plus className="w-4 h-4" /> {t("admin.newQuestionnaire")}
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="q-filters">
        {FILTERS.map((f) => {
          const active = filter === f;
          const label = t(`admin.filter${f.charAt(0).toUpperCase() + f.slice(1)}`);
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`q-filter-${f}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            >
              {label} <span className={`ms-1.5 text-xs ${active ? "text-slate-300" : "text-slate-500"}`}>{counts[f] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {filter === "archived" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-2 items-start" data-testid="q-archived-banner">
          <Archive className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{t("admin.archivedNotice")}</div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="tsw-card p-10 text-center text-slate-500" data-testid="q-empty">
          {t("phase2.noQuestionnairesFiltered")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((q) => {
            const responseCount = q.response_count ?? 0;
            const archived = q.status === "archived";
            return (
              <div key={q.id} className="tsw-card p-5 flex flex-col gap-3" data-testid={`q-row-${q.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-semibold text-slate-900">{tQuestionnaireTitle(q)}</div>
                    <div className="text-xs text-slate-500 mt-1">v{q.version} · {q.questions?.length || 0} {t("phase2.questionsLabel")}</div>
                    <div className="text-xs text-slate-500 mt-0.5" data-testid={`q-response-count-${q.id}`}>
                      {responseCount === 0
                        ? t("admin.noResponses")
                        : formatTpl(t("admin.hasResponses"), responseCount)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {isOfficial(q) && !archived && (
                      <span className="tsw-chip bg-emerald-50 text-emerald-800 border border-emerald-300" data-testid={`q-official-${q.id}`} title={t("admin.officialProtected")}>
                        <ShieldCheck className="w-3 h-3 inline" /> {t("admin.classificationOfficial")}
                      </span>
                    )}
                    {q.is_internal && <span className="tsw-chip bg-amber-50 text-amber-700 border border-amber-200">{t("admin.filterInternal").toLowerCase()}</span>}
                    {!q.is_active && !archived && <span className="tsw-chip bg-slate-100 text-slate-600">{t("admin.statusInactive").toLowerCase()}</span>}
                    {archived && <span className="tsw-chip bg-rose-50 text-rose-700 border border-rose-200" data-testid={`q-archived-${q.id}`}>{t("admin.archived")}</span>}
                    {isDemo(q) && !archived && <span className="tsw-chip bg-violet-50 text-violet-700 border border-violet-200"><FlaskConical className="w-3 h-3 inline" /> {t("admin.filterDemo").toLowerCase()}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(q.profile_keys || []).map((p) => {
                    const lbl = t(`profiles.${p}`);
                    return <span key={p} className="tsw-chip bg-emerald-50 text-emerald-700" title={p}>{lbl === `profiles.${p}` ? p : lbl}</span>;
                  })}
                </div>

                <div className="flex items-center gap-2 mt-auto pt-2 flex-wrap">
                  {!archived && (
                    <Link to={`/admin/questionnaires/${q.id}`} className="tsw-btn-secondary text-sm flex-1 justify-center" data-testid={`q-edit-${q.id}`}>
                      <ExternalLink className="w-4 h-4" /> {t("common.edit")}
                    </Link>
                  )}
                  {!archived && (
                    <button onClick={() => cloneQuestionnaire(q.id)} className="tsw-btn-ghost text-sm" title="Clone" data-testid={`q-clone-${q.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  {!archived && (
                    <button onClick={() => downloadResponses(q.id)} className="tsw-btn-ghost text-sm" data-testid={`q-export-${q.id}`} title="CSV">
                      <Download className="w-4 h-4" />
                    </button>
                  )}

                  {/* Archive / Restore / Delete */}
                  {archived ? (
                    <button
                      onClick={() => askConfirm("restore", q)}
                      className="tsw-btn-secondary text-sm flex-1 justify-center"
                      data-testid={`q-restore-${q.id}`}
                    >
                      <ArchiveRestore className="w-4 h-4" /> {t("admin.restore")}
                    </button>
                  ) : (() => {
                    const blockKey = deleteBlockReasonKey(q, responseCount);
                    const canDelete = !blockKey;
                    return (
                      <>
                        {/* Delete button: ALWAYS visible so admins know it
                            exists; disabled with a tooltip explaining why
                            when the questionnaire is protected. */}
                        <button
                          onClick={() => canDelete && askConfirm("delete", q)}
                          className={`tsw-btn-ghost text-sm ${canDelete ? "text-rose-600 hover:bg-rose-50" : "text-slate-300 cursor-not-allowed"}`}
                          disabled={!canDelete}
                          data-testid={`q-delete-${q.id}`}
                          title={blockKey ? t(blockKey) : t("admin.deleteQuestionnaire")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {/* Archive button: shown alongside Delete whenever
                            archive is the safe path forward (responses, or
                            protected items). Hidden only for fully-deletable
                            inactive demos to keep the UI minimal. */}
                        {!canDelete && (
                          <button
                            onClick={() => askConfirm("archive", q)}
                            className="tsw-btn-ghost text-sm text-amber-700 hover:bg-amber-50"
                            data-testid={`q-archive-${q.id}`}
                            title={t("admin.archive")}
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation modal */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="q-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${confirm?.kind === "delete" ? "text-rose-600" : confirm?.kind === "archive" ? "text-amber-600" : "text-emerald-600"}`} />
              {confirm?.kind === "delete" && t("admin.confirmDeleteTitle")}
              {confirm?.kind === "archive" && t("admin.confirmArchiveTitle")}
              {confirm?.kind === "restore" && t("admin.confirmRestoreTitle")}
            </DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              {confirm?.kind === "delete" && t("admin.confirmDeleteBody")}
              {confirm?.kind === "archive" && formatTpl(t("admin.confirmArchiveBody"), confirm?.q?.response_count ?? 0)}
              {confirm?.kind === "restore" && t("admin.confirmRestoreBody")}
            </DialogDescription>
          </DialogHeader>
          {confirm?.q && (() => {
            const cq = confirm.q;
            const cKey = classificationKey(cq);
            const sKey = statusKey(cq);
            const rc = cq.response_count ?? 0;
            return (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-700 space-y-1.5" data-testid="q-confirm-preview">
                <div className="font-semibold text-slate-900">{tQuestionnaireTitle(cq)}</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="tsw-chip bg-white border border-slate-200">
                    <span className="text-slate-500">{t("admin.classification")}:</span>&nbsp;
                    <span className={`font-semibold ${isOfficial(cq) ? "text-emerald-700" : isDemo(cq) ? "text-violet-700" : cq.is_internal ? "text-amber-700" : "text-slate-700"}`} data-testid="q-confirm-classification">
                      {t(cKey)}
                    </span>
                  </span>
                  <span className="tsw-chip bg-white border border-slate-200">
                    <span className="text-slate-500">{t("admin.statusLabel")}:</span>&nbsp;
                    <span className="font-semibold text-slate-900" data-testid="q-confirm-status">{t(sKey)}</span>
                  </span>
                  <span className="tsw-chip bg-white border border-slate-200" data-testid="q-confirm-response-count">
                    <span className="text-slate-500">{t("admin.responses")}:</span>&nbsp;
                    <span className={`font-semibold ${rc > 0 ? "text-amber-700" : "text-slate-900"}`}>{rc}</span>
                  </span>
                </div>
                <div className="text-xs text-slate-500 pt-0.5">
                  {(cq.profile_keys || []).map((p) => {
                    const lbl = t(`profiles.${p}`);
                    return lbl === `profiles.${p}` ? p : lbl;
                  }).join(", ") || "—"}
                </div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => setConfirm(null)}
              className="tsw-btn-secondary"
              disabled={busy}
              data-testid="q-confirm-cancel"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={runConfirm}
              disabled={busy}
              data-testid="q-confirm-ok"
              className={
                confirm?.kind === "delete"
                  ? "tsw-btn-primary !bg-rose-600 hover:!bg-rose-700"
                  : confirm?.kind === "archive"
                    ? "tsw-btn-primary !bg-amber-600 hover:!bg-amber-700"
                    : "tsw-btn-primary"
              }
            >
              {busy
                ? t("common.saving")
                : confirm?.kind === "delete"
                  ? t("admin.deleteQuestionnaire")
                  : confirm?.kind === "archive"
                    ? t("admin.archive")
                    : t("admin.restore")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestionnaires;
